import {
    Box,
    Button,
    Flex,
    HStack,
    Modal,
    ModalBody,
    ModalContent,
    ModalOverlay,
    Text,
    VStack,
} from "@chakra-ui/react";
import React, { useEffect } from "react";
import { FiCheck, FiLock, FiZap } from "react-icons/fi";

/**
 * Modal shown when user has exceeded their analysis quota
 * Encourages upgrade with clear value proposition
 */
const QuotaExceededModal = ({ isOpen, onClose, onUpgrade, quota }) => {
    const { used, total, resetDate, tier } = quota || {};

    // Debug logging
    useEffect(() => {
        console.log('QuotaExceededModal isOpen:', isOpen);
    }, [isOpen]);

    const formatResetDate = (date) => {
        if (!date) return "soon";
        const resetDateObj = new Date(date);
        const now = new Date();
        const daysUntilReset = Math.ceil((resetDateObj - now) / (1000 * 60 * 60 * 24));

        if (daysUntilReset <= 0) return "today";
        if (daysUntilReset === 1) return "tomorrow";
        return `in ${daysUntilReset} days`;
    };

    // Don't render anything if not open
    if (!isOpen) return null;

    // Cleanup effect to ensure body scroll is restored
    useEffect(() => {
        return () => {
            // Ensure body overflow is restored when component unmounts
            document.body.style.overflow = 'unset';
        };
    }, []);

    const premiumFeatures = [
        "Unlimited job analyses",
        "Advanced skill matching",
        "Priority resume generation",
        "Premium templates",
        "Cover letter generation",
        "Priority support",
    ];

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            isCentered
            size="lg"
            blockScrollOnMount={true}
            returnFocusOnClose={true}
            closeOnOverlayClick={true}
        >
            <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(8px)" />
            <ModalContent
                bg="white"
                borderRadius="2xl"
                boxShadow="0 25px 50px -12px rgba(0, 0, 0, 0.25)"
                p={8}
                mx={4}
            >
                <ModalBody p={0}>
                    <VStack spacing={6} align="stretch">
                        {/* Icon & Title */}
                        <VStack spacing={3}>
                            <Flex
                                w="80px"
                                h="80px"
                                bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                                borderRadius="2xl"
                                align="center"
                                justify="center"
                                boxShadow="0 10px 30px -5px rgba(102, 126, 234, 0.4)"
                            >
                                <Box as={FiLock} size="36px" color="white" />
                            </Flex>

                            <VStack spacing={2}>
                                <Text
                                    fontSize="2xl"
                                    fontWeight="700"
                                    textAlign="center"
                                    letterSpacing="tight"
                                    color="gray.800"
                                >
                                    You've Reached Your Analysis Limit
                                </Text>
                                <Text fontSize="md" color="gray.600" textAlign="center">
                                    You've used <strong>{used}/{total}</strong> analyses this month
                                    {resetDate && (
                                        <>
                                            {" • "}Resets {formatResetDate(resetDate)}
                                        </>
                                    )}
                                </Text>
                            </VStack>
                        </VStack>

                        {/* Current Plan Badge */}
                        <Box
                            bg="gray.50"
                            borderRadius="xl"
                            p={4}
                            borderLeft="3px solid"
                            borderColor="gray.300"
                        >
                            <HStack spacing={2}>
                                <Text fontSize="sm" color="gray.600" fontWeight="500">
                                    Current Plan:
                                </Text>
                                <Text
                                    fontSize="sm"
                                    fontWeight="700"
                                    color="gray.800"
                                    textTransform="capitalize"
                                >
                                    {tier || "Free"}
                                </Text>
                            </HStack>
                        </Box>

                        {/* Premium Features */}
                        <Box>
                            <HStack spacing={2} mb={3}>
                                <Box as={FiZap} color="orange.500" size="18px" />
                                <Text fontSize="md" fontWeight="700" color="gray.800">
                                    Upgrade to Premium
                                </Text>
                            </HStack>
                            <VStack spacing={2} align="stretch">
                                {premiumFeatures.map((feature, index) => (
                                    <HStack key={index} spacing={3}>
                                        <Flex
                                            w="20px"
                                            h="20px"
                                            bg="green.100"
                                            borderRadius="full"
                                            align="center"
                                            justify="center"
                                            flexShrink={0}
                                        >
                                            <Box as={FiCheck} size="12px" color="green.600" />
                                        </Flex>
                                        <Text fontSize="sm" color="gray.700" fontWeight="500">
                                            {feature}
                                        </Text>
                                    </HStack>
                                ))}
                            </VStack>
                        </Box>

                        {/* CTA Buttons */}
                        <VStack spacing={3} pt={2}>
                            <Button
                                w="full"
                                size="lg"
                                bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                                color="white"
                                _hover={{
                                    transform: "translateY(-2px)",
                                    boxShadow: "0 12px 24px -10px rgba(102, 126, 234, 0.6)",
                                }}
                                _active={{
                                    transform: "translateY(0)",
                                }}
                                transition="all 0.2s ease"
                                onClick={onUpgrade}
                                leftIcon={<FiZap />}
                                fontWeight="600"
                            >
                                View Plans & Pricing
                            </Button>
                            <Button
                                w="full"
                                size="md"
                                variant="ghost"
                                onClick={onClose}
                                color="gray.600"
                                fontWeight="500"
                                _hover={{ bg: "gray.100" }}
                            >
                                Maybe Later
                            </Button>
                        </VStack>
                    </VStack>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default QuotaExceededModal;
